import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { accessControlApi, rolesApi, usersApi, organizationsApi, objectsApi } from "@/services/api-v2.service";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Trash2, Plus } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { 
  DataAccessTemplate, 
  DataAccessTemplateCreate,
  ObjectAccessPolicy, 
  ObjectAccessPolicyCreate,
  LayerAccessPolicy,
  LayerAccessPolicyCreate,
  Role,
  User,
  Organization,
  GeoObject
} from "@/types/api-v2";

export default function DataAccessManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Новый шаблон доступа
  const [newTemplate, setNewTemplate] = useState({
    name: "",
    description: "",
    isPublic: false,
    allowExport: false,
    allowEdit: false,
    allowDelete: false,
    config: {}
  });

  // Новая политика доступа к объектам
  const [newObjectPolicy, setNewObjectPolicy] = useState({
    objectId: "",
    roleId: "",
    userId: "",
    organizationId: "",
    templateId: "",
    canView: false,
    canEdit: false,
    canDelete: false,
    canExport: false,
    canShare: false
  });

  // Новая политика доступа к слоям
  const [newLayerPolicy, setNewLayerPolicy] = useState({
    layerName: "",
    roleId: "",
    userId: "",
    organizationId: "",
    templateId: "",
    canView: false,
    canEdit: false,
    canDelete: false,
    canExport: false,
    canShare: false
  });

  // Загрузка данных
  const { data: templates = [] } = useQuery<DataAccessTemplate[]>({
    queryKey: ["access-control", "templates"],
    queryFn: () => accessControlApi.templates.list(),
  });

  const { data: objectPolicies = [] } = useQuery<ObjectAccessPolicy[]>({
    queryKey: ["access-control", "object-policies"],
    queryFn: () => accessControlApi.objectPolicies.list(),
  });

  const { data: layerPolicies = [] } = useQuery<LayerAccessPolicy[]>({
    queryKey: ["access-control", "layer-policies"],
    queryFn: () => accessControlApi.layerPolicies.list(),
  });

  const { data: roles = [] } = useQuery<Role[]>({
    queryKey: ["roles"],
    queryFn: () => rolesApi.list(),
  });

  const { data: users = [] } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: () => usersApi.list(),
  });

  const { data: organizations = [] } = useQuery<Organization[]>({
    queryKey: ["organizations"],
    queryFn: () => organizationsApi.list(),
  });

  const { data: objects = [] } = useQuery<GeoObject[]>({
    queryKey: ["objects"],
    queryFn: () => objectsApi.list(),
  });

  // Мутации для шаблонов доступа
  const createTemplateMutation = useMutation({
    mutationFn: async (template: DataAccessTemplateCreate) => {
      return await accessControlApi.templates.create(template);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access-control", "templates"] });
      setNewTemplate({
        name: "",
        description: "",
        isPublic: false,
        allowExport: false,
        allowEdit: false,
        allowDelete: false,
        config: {}
      });
      toast({
        title: "Шаблон доступа создан",
        description: "Новый шаблон доступа успешно создан",
      });
    },
  });

  const deleteTemplateMutation = useMutation({
    mutationFn: async (id: number) => {
      await accessControlApi.templates.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access-control", "templates"] });
      toast({
        title: "Шаблон удален",
        description: "Шаблон доступа успешно удален",
      });
    },
  });

  // Мутации для политик доступа к объектам
  const createObjectPolicyMutation = useMutation({
    mutationFn: async (policy: any) => {
      const cleanPolicy: ObjectAccessPolicyCreate = {
        objectId: policy.object_id ? parseInt(policy.object_id) : parseInt(policy.objectId),
        roleId: policy.role_id ? parseInt(policy.role_id) : (policy.roleId ? parseInt(policy.roleId) : null),
        organizationId: policy.organization_id ? parseInt(policy.organization_id) : (policy.organizationId ? parseInt(policy.organizationId) : null),
        templateId: policy.templateId ? parseInt(policy.templateId) : null,
        userId: policy.userId || null,
        canView: policy.canView || false,
        canEdit: policy.canEdit || false,
        canDelete: policy.canDelete || false,
        canExport: policy.canExport || false,
        canShare: policy.canShare || false,
      };
      return await accessControlApi.objectPolicies.create(cleanPolicy);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access-control", "object-policies"] });
      setNewObjectPolicy({
        objectId: "",
        roleId: "",
        userId: "",
        organizationId: "",
        templateId: "",
        canView: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
        canShare: false
      });
      toast({
        title: "Политика доступа создана",
        description: "Новая политика доступа к объекту успешно создана",
      });
    },
  });

  const deleteObjectPolicyMutation = useMutation({
    mutationFn: async (id: number) => {
      await accessControlApi.objectPolicies.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access-control", "object-policies"] });
      toast({
        title: "Политика удалена",
        description: "Политика доступа успешно удалена",
      });
    },
  });

  // Мутации для политик доступа к слоям
  const createLayerPolicyMutation = useMutation({
    mutationFn: async (policy: any) => {
      const cleanPolicy: LayerAccessPolicyCreate = {
        geometryId: policy.geometryId ? parseInt(policy.geometryId) : (policy.geometry_id ? parseInt(policy.geometry_id) : 0),
        roleId: policy.role_id ? parseInt(policy.role_id) : (policy.roleId ? parseInt(policy.roleId) : null),
        organizationId: policy.organization_id ? parseInt(policy.organization_id) : (policy.organizationId ? parseInt(policy.organizationId) : null),
        templateId: policy.templateId ? parseInt(policy.templateId) : null,
        userId: policy.userId || null,
        canView: policy.canView || false,
        canEdit: policy.canEdit || false,
        canDelete: policy.canDelete || false,
        canExport: policy.canExport || false,
      };
      return await accessControlApi.layerPolicies.create(cleanPolicy);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access-control", "layer-policies"] });
      setNewLayerPolicy({
        layerName: "",
        roleId: "",
        userId: "",
        organizationId: "",
        templateId: "",
        canView: false,
        canEdit: false,
        canDelete: false,
        canExport: false,
        canShare: false
      });
      toast({
        title: "Политика доступа создана",
        description: "Новая политика доступа к слою успешно создана",
      });
    },
  });

  const deleteLayerPolicyMutation = useMutation({
    mutationFn: async (id: number) => {
      await accessControlApi.layerPolicies.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["access-control", "layer-policies"] });
      toast({
        title: "Политика удалена",
        description: "Политика доступа к слою успешно удалена",
      });
    },
  });

  // Обработчики форм
  const handleCreateTemplate = (e: React.FormEvent) => {
    e.preventDefault();
    const config = {
      level: newTemplate.isPublic ? "public" : "restricted",
      restrictions: []
    };
    createTemplateMutation.mutate({ ...newTemplate, config });
  };

  const handleCreateObjectPolicy = (e: React.FormEvent) => {
    e.preventDefault();
    createObjectPolicyMutation.mutate(newObjectPolicy);
  };

  const handleCreateLayerPolicy = (e: React.FormEvent) => {
    e.preventDefault();
    createLayerPolicyMutation.mutate(newLayerPolicy);
  };

  // Функции для получения имен
  const getRoleName = (roleId: number | null) => {
    if (!roleId) return "Не указана";
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : `Роль ${roleId}`;
  };

  const getUserName = (userId: string | null) => {
    if (!userId) return "Не указан";
    const user = users.find(u => u.id === userId);
    return user ? user.username : userId;
  };

  const getOrganizationName = (orgId: number | null) => {
    if (!orgId) return "Не указана";
    const org = organizations.find(o => o.id === orgId);
    return org ? org.name : `Орган управления ${orgId}`;
  };

  const getObjectName = (objectId: number | null) => {
    if (!objectId) return "Не указан";
    const obj = objects.find(o => o.id === objectId);
    return obj ? obj.name : `Объект ${objectId}`;
  };

  const getTemplateName = (templateId: number | null) => {
    if (!templateId) return "Не указан";
    const template = templates.find(t => t.id === templateId);
    return template ? template.name : `Шаблон ${templateId}`;
  };

  return (
    <div className="space-y-6">
      <Tabs defaultValue="templates">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="templates">Шаблоны доступа</TabsTrigger>
          <TabsTrigger value="object-policies">Политики объектов</TabsTrigger>
          <TabsTrigger value="layer-policies">Политики слоев</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="space-y-4">
          {/* Форма создания шаблона доступа */}
          <Card>
            <CardHeader>
              <CardTitle>Создать шаблон доступа</CardTitle>
              <CardDescription>
                Шаблоны доступа определяют предустановленные политики безопасности
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateTemplate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="template-name">Название шаблона</Label>
                    <Input
                      id="template-name"
                      value={newTemplate.name}
                      onChange={(e) => setNewTemplate(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Например: Внутренний доступ"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Публичный доступ</Label>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newTemplate.isPublic}
                        onCheckedChange={(checked) => setNewTemplate(prev => ({ ...prev, isPublic: checked }))}
                      />
                      <span className="text-sm text-muted-foreground">
                        Доступно всем пользователям
                      </span>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="template-description">Описание</Label>
                  <Textarea
                    id="template-description"
                    value={newTemplate.description}
                    onChange={(e) => setNewTemplate(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Описание условий доступа..."
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newTemplate.allowExport}
                      onCheckedChange={(checked) => setNewTemplate(prev => ({ ...prev, allowExport: checked }))}
                    />
                    <Label>Разрешить экспорт</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newTemplate.allowEdit}
                      onCheckedChange={(checked) => setNewTemplate(prev => ({ ...prev, allowEdit: checked }))}
                    />
                    <Label>Разрешить редактирование</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newTemplate.allowDelete}
                      onCheckedChange={(checked) => setNewTemplate(prev => ({ ...prev, allowDelete: checked }))}
                    />
                    <Label>Разрешить удаление</Label>
                  </div>
                </div>

                <Button type="submit" disabled={createTemplateMutation.isPending}>
                  <Plus className="w-4 h-4 mr-2" />
                  {createTemplateMutation.isPending ? "Создание..." : "Создать шаблон"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Список шаблонов доступа */}
          <Card>
            <CardHeader>
              <CardTitle>Существующие шаблоны доступа</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {templates.length === 0 ? (
                  <p className="text-muted-foreground">Шаблоны доступа не созданы</p>
                ) : (
                  templates.map((template) => (
                    <div key={template.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h4 className="font-medium">{template.name}</h4>
                          {template.isPublic && <Badge variant="secondary">Публичный</Badge>}
                          {template.allowExport && <Badge variant="outline">Экспорт</Badge>}
                          {template.allowEdit && <Badge variant="outline">Редактирование</Badge>}
                          {template.allowDelete && <Badge variant="destructive">Удаление</Badge>}
                        </div>
                        {template.description && (
                          <p className="text-sm text-muted-foreground mt-1">{template.description}</p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteTemplateMutation.mutate(template.id)}
                        disabled={deleteTemplateMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="object-policies" className="space-y-4">
          {/* Форма создания политики доступа к объектам */}
          <Card>
            <CardHeader>
              <CardTitle>Создать политику доступа к объекту</CardTitle>
              <CardDescription>
                Настройте гранулярные права доступа к конкретным объектам
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateObjectPolicy} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="object-select">Объект</Label>
                    <Select value={newObjectPolicy.objectId} onValueChange={(value) => setNewObjectPolicy(prev => ({ ...prev, objectId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите объект" />
                      </SelectTrigger>
                      <SelectContent>
                        {objects.map((obj) => (
                          <SelectItem key={obj.id} value={obj.id.toString()}>
                            {obj.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="template-select">Шаблон доступа</Label>
                    <Select value={newObjectPolicy.templateId} onValueChange={(value) => setNewObjectPolicy(prev => ({ ...prev, templateId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите шаблон" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((template) => (
                          <SelectItem key={template.id} value={template.id.toString()}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="role-select">Роль</Label>
                    <Select value={newObjectPolicy.roleId} onValueChange={(value) => setNewObjectPolicy(prev => ({ ...prev, roleId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите роль" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((role) => (
                          <SelectItem key={role.id} value={role.id.toString()}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="user-select">Пользователь</Label>
                    <Select value={newObjectPolicy.userId} onValueChange={(value) => setNewObjectPolicy(prev => ({ ...prev, userId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите пользователя" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.username}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="org-select">Орган управления</Label>
                    <Select value={newObjectPolicy.organizationId} onValueChange={(value) => setNewObjectPolicy(prev => ({ ...prev, organizationId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите орган управления" />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id.toString()}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-5 gap-4">
                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newObjectPolicy.canView}
                      onCheckedChange={(checked) => setNewObjectPolicy(prev => ({ ...prev, canView: checked }))}
                    />
                    <Label>Просмотр</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newObjectPolicy.canEdit}
                      onCheckedChange={(checked) => setNewObjectPolicy(prev => ({ ...prev, canEdit: checked }))}
                    />
                    <Label>Редактирование</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newObjectPolicy.canDelete}
                      onCheckedChange={(checked) => setNewObjectPolicy(prev => ({ ...prev, canDelete: checked }))}
                    />
                    <Label>Удаление</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newObjectPolicy.canExport}
                      onCheckedChange={(checked) => setNewObjectPolicy(prev => ({ ...prev, canExport: checked }))}
                    />
                    <Label>Экспорт</Label>
                  </div>

                  <div className="flex items-center space-x-2">
                    <Switch
                      checked={newObjectPolicy.canShare}
                      onCheckedChange={(checked) => setNewObjectPolicy(prev => ({ ...prev, canShare: checked }))}
                    />
                    <Label>Отправка</Label>
                  </div>
                </div>

                <Button type="submit" disabled={createObjectPolicyMutation.isPending}>
                  <Plus className="w-4 h-4 mr-2" />
                  {createObjectPolicyMutation.isPending ? "Создание..." : "Создать политику"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Список политик доступа к объектам */}
          <Card>
            <CardHeader>
              <CardTitle>Существующие политики доступа к объектам</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {objectPolicies.length === 0 ? (
                  <p className="text-muted-foreground">Политики доступа не созданы</p>
                ) : (
                  objectPolicies.map((policy) => (
                    <div key={policy.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <Badge variant="outline">{getObjectName(policy.objectId ?? null)}</Badge>
                          {policy.roleId && <Badge>{getRoleName(policy.roleId)}</Badge>}
                          {policy.userId && <Badge variant="secondary">{getUserName(policy.userId)}</Badge>}
                          {policy.organizationId && <Badge variant="outline">{getOrganizationName(policy.organizationId)}</Badge>}
                        </div>
                        <div className="flex items-center gap-1">
                          {policy.canView && <Badge variant="outline" className="text-xs">Просмотр</Badge>}
                          {policy.canEdit && <Badge variant="outline" className="text-xs">Редактирование</Badge>}
                          {policy.canDelete && <Badge variant="destructive" className="text-xs">Удаление</Badge>}
                          {policy.canExport && <Badge variant="outline" className="text-xs">Экспорт</Badge>}
                          {policy.canShare && <Badge variant="outline" className="text-xs">Отправка</Badge>}
                        </div>
                        {policy.templateId && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Шаблон: {getTemplateName(policy.templateId)}
                          </p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteObjectPolicyMutation.mutate(policy.id)}
                        disabled={deleteObjectPolicyMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="layer-policies" className="space-y-4">
          {/* Форма создания политики доступа к слою */}
          <Card>
            <CardHeader>
              <CardTitle>Создать политику доступа к слою</CardTitle>
              <CardDescription>
                Управление доступом к отдельным геометрическим слоям
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateLayerPolicy} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="layer-name">Название слоя</Label>
                    <Input
                      id="layer-name"
                      value={newLayerPolicy.layerName}
                      onChange={(e) => setNewLayerPolicy(prev => ({ ...prev, layerName: e.target.value }))}
                      placeholder="Например: buildings, roads"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="layer-template-select">Шаблон доступа</Label>
                    <Select value={newLayerPolicy.templateId} onValueChange={(value) => setNewLayerPolicy(prev => ({ ...prev, templateId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите шаблон" />
                      </SelectTrigger>
                      <SelectContent>
                        {templates.map((template) => (
                          <SelectItem key={template.id} value={template.id.toString()}>
                            {template.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="layer-role-select">Роль</Label>
                    <Select value={newLayerPolicy.roleId} onValueChange={(value) => setNewLayerPolicy(prev => ({ ...prev, roleId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите роль" />
                      </SelectTrigger>
                      <SelectContent>
                        {roles.map((role) => (
                          <SelectItem key={role.id} value={role.id.toString()}>
                            {role.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="layer-user-select">Пользователь</Label>
                    <Select value={newLayerPolicy.userId} onValueChange={(value) => setNewLayerPolicy(prev => ({ ...prev, userId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите пользователя" />
                      </SelectTrigger>
                      <SelectContent>
                        {users.map((user) => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.username}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="layer-org-select">Орган управления</Label>
                    <Select value={newLayerPolicy.organizationId} onValueChange={(value) => setNewLayerPolicy(prev => ({ ...prev, organizationId: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Выберите орган управления" />
                      </SelectTrigger>
                      <SelectContent>
                        {organizations.map((org) => (
                          <SelectItem key={org.id} value={org.id.toString()}>
                            {org.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Права доступа</Label>
                  <div className="flex gap-4 flex-wrap">
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newLayerPolicy.canView}
                        onCheckedChange={(checked) => setNewLayerPolicy(prev => ({ ...prev, canView: checked }))}
                      />
                      <span className="text-sm">Просмотр</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newLayerPolicy.canEdit}
                        onCheckedChange={(checked) => setNewLayerPolicy(prev => ({ ...prev, canEdit: checked }))}
                      />
                      <span className="text-sm">Редактирование</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newLayerPolicy.canDelete}
                        onCheckedChange={(checked) => setNewLayerPolicy(prev => ({ ...prev, canDelete: checked }))}
                      />
                      <span className="text-sm">Удаление</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newLayerPolicy.canExport}
                        onCheckedChange={(checked) => setNewLayerPolicy(prev => ({ ...prev, canExport: checked }))}
                      />
                      <span className="text-sm">Экспорт</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Switch
                        checked={newLayerPolicy.canShare}
                        onCheckedChange={(checked) => setNewLayerPolicy(prev => ({ ...prev, canShare: checked }))}
                      />
                      <span className="text-sm">Совместное использование</span>
                    </div>
                  </div>
                </div>

                <Button type="submit" disabled={createLayerPolicyMutation.isPending}>
                  {createLayerPolicyMutation.isPending ? "Создание..." : "Создать политику"}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Список существующих политик доступа к слоям */}
          <Card>
            <CardHeader>
              <CardTitle>Существующие политики доступа к слоям</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {layerPolicies.length === 0 ? (
                  <p className="text-muted-foreground">Политики доступа к слоям не настроены</p>
                ) : (
                  layerPolicies.map((policy) => (
                    <div key={policy.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline">{policy.layerName || "Все слои"}</Badge>
                          <Badge variant="outline">{getRoleName(policy.roleId ?? null)}</Badge>
                          <Badge variant="outline">{getUserName(policy.userId ?? null)}</Badge>
                          <Badge variant="outline">{getOrganizationName(policy.organizationId ?? null)}</Badge>
                          <Badge variant="outline">{getTemplateName(policy.templateId ?? null)}</Badge>
                        </div>
                        <div className="flex gap-2 text-xs text-muted-foreground">
                          {policy.canView && <Badge variant="secondary">Просмотр</Badge>}
                          {policy.canEdit && <Badge variant="secondary">Редактирование</Badge>}
                          {policy.canDelete && <Badge variant="secondary">Удаление</Badge>}
                          {policy.canExport && <Badge variant="secondary">Экспорт</Badge>}
                          {policy.canShare && <Badge variant="secondary">Совместное использование</Badge>}
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteLayerPolicyMutation.mutate(policy.id)}
                        disabled={deleteLayerPolicyMutation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}